"use client";

import { useMarket } from "@features/market/useMarket";
import { findMarketByCode } from "@features/market/config/market-config";
import { useQueryClient } from "@tanstack/react-query";
import { deleteCookie, getCookie, hasCookie, setCookie } from "cookies-next";
import { usePathname, useRouter } from "next/navigation";
import { useCallback, useMemo } from "react";

import { COOKIE_MAX_AGE } from "@/utils/const";

type MarketStatePayload =
  | {
      market: string;
      link?: never;
    }
  | {
      market?: never;
      link: string;
    };

function pathWithoutSiteLocale(pathname: string, siteLocale: string) {
  if (!siteLocale) return pathname || "/";
  const prefix = `/${siteLocale}`;
  if (pathname === prefix) return "/";
  if (pathname.startsWith(`${prefix}/`)) {
    return pathname.slice(prefix.length) || "/";
  }
  return pathname || "/";
}

export function useMarketSelector() {
  const router = useRouter();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const market = useMarket();

  const suggested = useMemo(() => {
    if (!hasCookie("ng_suggestion")) return undefined;
    const cookie = getCookie("ng_suggestion");
    if (!cookie) return undefined;
    return findMarketByCode(cookie.toString());
  }, []);

  const dismissed = useMemo(() => {
    if (!hasCookie("ng_dismissed")) return false;
    const cookie = getCookie("ng_dismissed");
    return Boolean(cookie);
  }, []);

  const onSave = useCallback(
    (payload: MarketStatePayload) => {
      if (payload.link) {
        router.push(payload.link);
      } else if (payload.market) {
        const next = findMarketByCode(payload.market);
        if (hasCookie("ng_suggestion")) deleteCookie("ng_suggestion");
        if (hasCookie("ng_dismissed")) deleteCookie("ng_dismissed");
        setCookie("ng_market", payload.market, {
          maxAge: COOKIE_MAX_AGE,
        });
        setCookie("ng_lang", next.languageCode, {
          maxAge: COOKIE_MAX_AGE,
        });
        if (market.siteLocale !== next.siteLocale) {
          const prefix = next.siteLocale ? `/${next.siteLocale}` : "";
          const cleanPath = pathWithoutSiteLocale(pathname, market.siteLocale);
          // Using router.push here breaks imbox-widget, hence the window.location.href
          window.location.href =
            cleanPath === "/checkout" ? `${prefix}/checkout` : prefix || "/";
        }
      }
    },
    [router, pathname, market.siteLocale],
  );

  const onClose = () => {
    if (hasCookie("ng_suggestion")) {
      setCookie("ng_dismissed", true, {
        maxAge: COOKIE_MAX_AGE,
      });
    }
    queryClient.setQueryData(["marketSelector"], false);
  };

  return {
    onSave,
    onClose,
    state: {
      market,
      suggested,
      dismissed,
    },
  };
}
